import { useTranslation } from 'react-i18next';
import { useSession } from '@/lib/auth-client';
import { useAnnotationsStore } from './store';

/**
 * F5.7 — Floating toolbar (logged-in only) that toggles "drop a note" mode.
 *
 * One button for now (point notes). Areas/lines are reserved for a future
 * F5.7+ iteration once Marat actually needs them.
 *
 * Placement: bottom-left of the map, stacked above the FeedbackButton.
 * Top-right was the previous home but DetailsPanel expands to ~80vh and
 * its header collided with this button. Bottom-right hosts DetailsPanel +
 * AiHelperBubble. Top-left is the SearchBar / LayerSwitcher / FiltersPanel
 * column. Bottom-left is the only fully free corner.
 */

export function AnnotationToolbar() {
  const { t } = useTranslation();
  const { data: session } = useSession();
  const placing = useAnnotationsStore((s) => s.placing);
  const start = useAnnotationsStore((s) => s.startPlacing);
  const cancel = useAnnotationsStore((s) => s.cancelPlacing);

  if (!session) return null;

  return (
    <div
      className="absolute bottom-20 left-4 z-[1000]"
      role="toolbar"
      aria-label={t('annotations.toolbar', { defaultValue: 'Annotations' })}
    >
      <button
        type="button"
        onClick={placing ? cancel : start}
        aria-pressed={placing}
        title={
          placing
            ? t('annotations.cancelHint', { defaultValue: 'Click again to cancel' })
            : t('annotations.startHint', { defaultValue: 'Click anywhere on the map to add a note' })
        }
        className={`inline-flex items-center gap-1 rounded-lg px-3 py-2 text-sm font-medium shadow-md backdrop-blur-sm ${
          placing
            ? 'bg-amber-500 text-white hover:bg-amber-600'
            : 'bg-white/90 text-slate-700 hover:bg-white'
        }`}
      >
        <span aria-hidden="true">📝</span>
        <span className="hidden sm:inline">
          {placing
            ? t('annotations.placing', { defaultValue: 'Click map to place…' })
            : t('annotations.add', { defaultValue: 'Add note' })}
        </span>
      </button>
    </div>
  );
}
